import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Printer, Tags, Trash2, X } from 'lucide-react'
import { useRef, useState, useSyncExternalStore, type ReactNode } from 'react'

import type {
  LabelTemplateSummaryType,
  VariantLabelInfo,
} from '@aeris/contracts'
import {
  compare,
  fromString,
  formatMoney,
  UNIT_PRICE_SCALE,
  type Decimal,
} from '@aeris/money'
import {
  labelFor,
  labelSheetsFor,
  renderLabelSheetHtml,
  renderLayoutSheetHtml,
} from '@aeris/printing'
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  InlineAlert,
  Input,
  Page,
  PageHeader,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { LabelSheetPreview } from '../components/label-sheet-preview.tsx'
import { PrintSettingsHint } from '../components/print-settings-hint.tsx'
import {
  fetchLabelInfoByCodes,
  fetchVariantLabelInfo,
} from '../lib/api/catalog.ts'
import { fetchLabelTemplates } from '../lib/api/labels.ts'
import { useAuth } from '../lib/auth-context.ts'
import { printHtmlDocument } from '../lib/label-print.ts'
import {
  addToLabelQueue,
  clearLabelQueue,
  labelCount,
  LABEL_QUEUE_MAX,
  LABEL_QUEUE_ORDERS,
  readLabelQueue,
  removeFromLabelQueue,
  setLabelQuantity,
  sortLabelQueue,
  subscribeToLabelQueue,
  type LabelQueueItem,
  type LabelQueueOrder,
} from '../lib/label-queue.ts'
import { labelSheetSummary } from '../lib/label-sheet-summary.ts'
import { readLabelTemplate } from '../lib/label-template.ts'
import { parseScanInput } from '../lib/pos-input.ts'
import { printedOnToday } from '../lib/printed-on.ts'
import { useScanOwner } from '../lib/scan-owner.ts'
import { timeAgo } from '../lib/time-ago.ts'
import { usePreference } from '../lib/use-preference.ts'

/**
 * A fila de etiquetas — F124, fatia 3.
 *
 * As quatro origens do módulo (ficha do produto, entrada de compra, lote do
 * catálogo, preço salvo) são quatro maneiras de encher **esta mesma lista** de
 * (variante, quantidade). Por isso são uma feature só e quatro botões baratos,
 * e não quatro fluxos.
 *
 * ## A pré-visualização é o mesmo código que imprime
 *
 * O quadro abaixo da tabela é um `<iframe>` alimentado por
 * `renderLabelSheetHtml` — literalmente a mesma função e o mesmo documento que
 * vão para a impressora. Uma pré-visualização desenhada à parte é uma segunda
 * implementação que concorda com a primeira até parar de concordar, e o lugar
 * onde isso é descoberto é o rolo de adesivo.
 */

/**
 * A ordem da fila é conveniência DESTA pessoa neste computador (F195), como a
 * própria fila: guardada no `localStorage`, e uma leitura que falha (aba
 * anônima, armazenamento bloqueado) é só a ordem de chegada.
 */
/**
 * A quantidade de etiquetas de uma linha — F248.
 *
 * Gravava a cada tecla: apagar o "1" para digitar "12" dava zero no meio do
 * caminho, e zero tira a linha da fila (`setLabelQuantity`). A linha sumia
 * debaixo do dedo. Agora o campo é texto até sair dele ou apertar Enter; vazio
 * ou zero volta ao que era — tirar da fila é o botão Tirar.
 */
const LabelQuantity = ({
  name,
  quantity,
  onCommit,
}: {
  readonly name: string
  readonly quantity: number
  readonly onCommit: (next: number) => void
}): ReactNode => {
  const [text, setText] = useState(String(quantity))

  const commit = (): void => {
    const next = Number.parseInt(text, 10)
    if (!Number.isFinite(next) || next < 1) {
      setText(String(quantity))
      return
    }
    if (next !== quantity) onCommit(next)
  }

  return (
    <Input
      inputMode="numeric"
      className="w-20"
      aria-label={`Quantidade de etiquetas de ${name}`}
      value={text}
      onChange={(event) => {
        setText(event.target.value.replace(/\D/g, ''))
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          commit()
        }
      }}
    />
  )
}

const ORDER_KEY = 'aeris.label-queue.order'

const readOrder = (): LabelQueueOrder => {
  try {
    const stored = window.localStorage.getItem(ORDER_KEY)
    return (
      LABEL_QUEUE_ORDERS.find((entry) => entry.value === stored)?.value ??
      'arrival'
    )
  } catch {
    return 'arrival'
  }
}

const saveOrder = (order: LabelQueueOrder): void => {
  try {
    window.localStorage.setItem(ORDER_KEY, order)
  } catch {
    // Vale para esta tela aberta, e é o que importa.
  }
}

/** Inteiro dentro dos limites, ou nulo — "abc" e "0" não são posição. */
const wholeBetween = (
  text: string,
  min: number,
  max: number
): number | null => {
  if (!/^\d+$/.test(text.trim())) return null
  const value = Number(text)
  return value >= min && value <= max ? value : null
}

const asDecimal = (
  value: string | null
): ReturnType<typeof fromString> | null =>
  value === null ? null : fromString(value, UNIT_PRICE_SCALE)

/** O preço guardado na fila e o de agora são o mesmo preço? */
const samePrice = (queued: string | null, now: string | null): boolean =>
  queued === null || now === null
    ? queued === now
    : compare(
        fromString(queued, UNIT_PRICE_SCALE),
        fromString(now, UNIT_PRICE_SCALE)
      ) === 0

/**
 * Quantas etiquetas `3*código` quer dizer.
 *
 * Inteiro, por divisão de BigInt e nunca por ponto flutuante: adesivo não se
 * imprime pela metade, então `2,5*ABC` são duas etiquetas. Menos de uma é uma —
 * quem bipou quer pelo menos a etiqueta que bipou.
 */
const wholeLabels = (quantity: Decimal): number => {
  const whole = Number(quantity.units / 10n ** BigInt(quantity.scale))
  if (!Number.isFinite(whole) || whole < 1) return 1
  return Math.min(whole, LABEL_QUEUE_MAX)
}

const QueueRow = ({
  item,
  current,
  missing,
  tenantId,
}: {
  item: LabelQueueItem
  /** Nome e preço de agora; indefinido enquanto a conferência não voltou. */
  current: VariantLabelInfo | undefined
  missing: boolean
  tenantId: string
}): ReactNode => {
  const queued = asDecimal(item.salePrice)
  const now = current === undefined ? queued : asDecimal(current.salePrice)
  const changed =
    current !== undefined && !samePrice(item.salePrice, current.salePrice)
  const gtin = current === undefined ? item.gtin : current.gtin

  return (
    <TableRow>
      <TableCell>
        <div className="flex flex-col">
          <span>{current?.name ?? item.name}</span>
          <span className="text-(length:--text-xs) text-(--text-muted)">
            {current?.sku ?? item.sku}
            {gtin === null ? '' : ` · ${gtin}`}
          </span>
          {missing ? (
            <Badge tone="danger" className="mt-1 self-start">
              produto não encontrado
            </Badge>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col items-start gap-0.5">
          {now === null ? (
            // Não é "R$ 0,00": um produto sem preço cadastrado existe (F123), e
            // o zero impresso é um zero que alguém cobra.
            <span className="text-(--text-muted)">sem preço</span>
          ) : (
            <span>{formatMoney(now)}</span>
          )}
          {changed ? (
            <span className="flex flex-wrap items-center gap-1 text-(length:--text-xs) text-(--text-muted)">
              era {queued === null ? 'sem preço' : formatMoney(queued)}
              <Badge tone="warning">preço atualizado</Badge>
            </span>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap text-(--text-secondary)">
        {item.addedAt === undefined ? '—' : timeAgo(item.addedAt)}
      </TableCell>
      <TableCell>
        <LabelQuantity
          // Uma mudança de fora (bipar o mesmo produto) recomeça o campo.
          key={item.quantity}
          name={item.name}
          quantity={item.quantity}
          onCommit={(next) => {
            setLabelQuantity(tenantId, item.variantId, next)
          }}
        />
      </TableCell>
      <TableCell className="text-right">
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Tirar ${item.name} da fila`}
          onClick={() => {
            removeFromLabelQueue(tenantId, item.variantId)
          }}
          leadingIcon={<X aria-hidden="true" className="h-3.5 w-3.5" />}
        >
          Tirar
        </Button>
      </TableCell>
    </TableRow>
  )
}

export const EtiquetasPage = (): ReactNode => {
  const { session } = useAuth()
  const tenantId = session?.context?.tenantId ?? null

  const items = useSyncExternalStore(subscribeToLabelQueue, () =>
    readLabelQueue(tenantId)
  )
  const [error, setError] = useState<string | null>(null)
  const [chosenId, setChosenId] = useState<string | null>(null)
  /* F195: começar na etiqueta N de uma folha já usada, e a ordem da fila. */
  const [startText, setStartText] = useState('1')
  const [order, setOrder] = useState<LabelQueueOrder>(readOrder)
  const [confirmingClear, setConfirmingClear] = useState(false)

  /*
   * O modelo escolhido sobrevive ao recarregar — e é o MESMO do diálogo do
   * catálogo (F184).
   *
   * A preferência `labels.lastTemplateId` existe desde a F127 e já é da PESSOA,
   * não do computador; esta tela era a única que não a usava, com `useState`
   * puro. O dono pediu para escolher a etiqueta ANTES de bipar: uma escolha que
   * se perde a cada F5 não é uma escolha, é uma pergunta repetida.
   */
  const lastTemplate = usePreference('labels.lastTemplateId', {
    templateId: null,
  })

  /* O campo de bipar (F184). */
  const scanRef = useRef<HTMLInputElement>(null)
  const [scan, setScan] = useState('')
  const [scanError, setScanError] = useState<string | null>(null)
  const [scanning, setScanning] = useState(false)

  const templates = useQuery({
    queryKey: ['label-templates'],
    queryFn: async ({ signal }) => await fetchLabelTemplates('active', signal),
    // O vocabulário de modelos da loja não muda no meio de uma impressão.
    staleTime: 5 * 60_000,
  })

  /*
   * O nome e o preço de AGORA (F136).
   *
   * A fila fica guardada no computador, então pode ter dias — e uma remarcação
   * no meio. A tela relê as variantes ao abrir: a etiqueta e a prévia saem com
   * o preço atual, e cada linha que mudou diz "era R$ X". Produto que não volta
   * (inativo, apagado) não é impresso com dado velho: fica marcado e fora da
   * folha. Em lotes de 50 ids por pedido, para a URL não crescer com a fila.
   */
  const ids = items.map((item) => item.variantId)
  const fresh = useQuery({
    queryKey: ['label-info', ids],
    queryFn: async ({ signal }) => {
      const chunks: string[][] = []
      for (let at = 0; at < ids.length; at += 50) {
        chunks.push(ids.slice(at, at + 50))
      }
      const lists = await Promise.all(
        chunks.map(async (chunk) => await fetchVariantLabelInfo(chunk, signal))
      )
      return new Map(
        lists
          .flatMap((list) => list.items)
          .map((info) => [info.variantId, info])
      )
    },
    enabled: ids.length > 0,
    staleTime: 30_000,
  })
  const currentOf = (variantId: string): VariantLabelInfo | undefined =>
    fresh.data?.get(variantId)
  const isMissing = (variantId: string): boolean =>
    fresh.data !== undefined && !fresh.data.has(variantId)

  const withCurrent = (item: LabelQueueItem): LabelQueueItem => {
    const info = currentOf(item.variantId)
    return info === undefined
      ? item
      : {
          ...item,
          name: info.name,
          sku: info.sku,
          gtin: info.gtin,
          salePrice: info.salePrice,
          brand: info.brand,
          unit: info.unit,
          supplierReference: info.supplierReference ?? null,
          location: info.location ?? null,
        }
  }
  /*
   * A ordem escolhida vale para a tabela E para a folha (F195): a pessoa cola
   * na ordem que vê. Ordena pelo nome e local de AGORA, que é o que sai
   * impresso; a linha da tabela continua sendo a da fila, que é o que sabe
   * dizer "era R$ X".
   */
  const ordered = sortLabelQueue(items.map(withCurrent), order)
  const queuedById = new Map(items.map((item) => [item.variantId, item]))
  const rows = ordered.flatMap((item) => {
    const queued = queuedById.get(item.variantId)
    return queued === undefined ? [] : [queued]
  })
  const printable = ordered.filter((item) => !isMissing(item.variantId))
  const changedCount = items.filter((item) => {
    const info = currentOf(item.variantId)
    return info !== undefined && !samePrice(item.salePrice, info.salePrice)
  }).length
  const missingItems = items.filter((item) => isMissing(item.variantId))

  const available = templates.data ?? []
  /*
   * O escolhido, o padrão da loja, ou nenhum — nessa ordem.
   *
   * "Nenhum" não é um erro: a loja que nunca importou um `.conf` imprime no
   * formato simples do Aeris, que é o que a F124 já fazia antes de os modelos
   * do GDOOR entrarem. Uma tela que exigisse modelo antes de imprimir deixaria
   * de funcionar para quem não veio do GDOOR.
   */
  const chosen: LabelTemplateSummaryType | undefined =
    available.find((entry) => entry.id === chosenId) ??
    available.find((entry) => entry.id === lastTemplate.value.templateId) ??
    available.find((entry) => entry.isDefault) ??
    available[0]

  /*
   * Bipar: uma leitura, uma resposta, o foco de volta no campo (F184).
   *
   * O molde é o do PDV: Enter resolve na hora, sem esperar espera de digitação,
   * porque o leitor termina a leitura com Enter e a espera só serviria para
   * quem digita devagar. `3*código` vem do mesmo `parseScanInput` que o caixa
   * já usa — é o atalho que todo balconista brasileiro conhece.
   *
   * Código que não existe NÃO abre diálogo: a linha fica vermelha e o foco
   * volta. Bipar é ritmo, e um diálogo por engano de leitura quebra a sequência
   * de vinte peças.
   */
  const submitScan = (text: string = scan): void => {
    const parsed = parseScanInput(text)
    if (parsed === null || tenantId === null) return

    // Limpo já: a próxima peça pode ser lida enquanto esta resolve.
    setScan('')
    setScanError(null)
    setScanning(true)

    fetchLabelInfoByCodes([parsed.code])
      .then((list) => {
        const found = list.items
        if (found.length === 0) {
          setScanError(`Nenhum produto com o código ${parsed.code}.`)
          return
        }
        /*
         * O mesmo código em duas peças — o SKU de uma é o código de barras da
         * outra. Raro, e o silêncio seria caro: imprimiria o adesivo da peça
         * errada, e ninguém confere adesivo depois de impresso.
         */
        if (found.length > 1) {
          setScanError(
            `O código ${parsed.code} serve a ${String(found.length)} produtos. Use o código do produto.`
          )
          return
        }
        const info = found[0]
        if (info === undefined) return
        const { dropped } = addToLabelQueue(tenantId, [
          {
            variantId: info.variantId,
            productId: info.productId,
            name: info.name,
            sku: info.sku,
            gtin: info.gtin,
            salePrice: info.salePrice,
            brand: info.brand,
            unit: info.unit,
            supplierReference: info.supplierReference ?? null,
            location: info.location ?? null,
            quantity: wholeLabels(parsed.quantity),
          },
        ])
        /*
         * A 201ª peça (F195): antes sumia calada, com o campo já limpo. Agora
         * o código VOLTA ao campo — se a pessoa ainda não bipou a próxima —
         * para não se perder enquanto ela imprime ou limpa a fila.
         */
        if (dropped > 0) {
          setScan((current) => (current === '' ? scan : current))
          setScanError(
            `A fila está cheia — ${String(LABEL_QUEUE_MAX)} produtos. Imprima ou limpe a fila antes de bipar outro.`
          )
        }
      })
      .catch((cause: unknown) => {
        setScanError(
          cause instanceof Error
            ? cause.message
            : 'Não foi possível procurar o código.'
        )
      })
      .finally(() => {
        setScanning(false)
        scanRef.current?.focus()
      })
  }

  // O bipe com o foco fora do campo também cai aqui (F206).
  useScanOwner((code) => {
    submitScan(code)
  })

  const template = readLabelTemplate()
  const total = labelCount(printable)

  /*
   * Uma etiqueta por unidade.
   *
   * A fila conta quantas o produto precisa; o documento é uma lista de
   * adesivos. Três de um produto são três entradas iguais, e é o `@page` que
   * decide onde cada uma cai no papel.
   */
  const stickers = printable.flatMap((item) =>
    Array.from({ length: item.quantity }, () => item)
  )
  const repeated = <T,>(
    make: (item: LabelQueueItem) => T,
    labels: number
  ): T[] => stickers.slice(0, labels).map(make)

  /*
   * Começar na etiqueta N (F195). A folha de adesivo meio usada ia para o lixo:
   * o diálogo de um produto sabia pular posições desde a F127, e a fila — onde
   * saem as folhas grandes — não passava o `startAt` que `labelSheetsFor` já
   * aceitava. Rolo e formato simples não têm posição.
   */
  const perSheet =
    chosen === undefined ? 1 : chosen.layout.columns * chosen.layout.rows
  const startAt =
    perSheet === 1 ? 1 : (wholeBetween(startText, 1, perSheet) ?? 1)
  const sheets =
    chosen === undefined
      ? labelSheetsFor({ columns: 1, rows: 1 }, total)
      : labelSheetsFor(chosen.layout, total, startAt)

  /*
   * O modelo da loja, quando existe; o formato simples do Aeris, quando não.
   *
   * "Não existe" não é erro: a loja que nunca importou um `.conf` continua
   * imprimindo como a F124 imprimia antes de os modelos do GDOOR entrarem. Uma
   * tela que exigisse modelo deixaria de funcionar para quem não veio do GDOOR.
   */
  const htmlFor = (labels: number, skip: number): string =>
    printable.length === 0
      ? ''
      : chosen === undefined
        ? renderLabelSheetHtml(
            repeated(
              (item) =>
                labelFor(
                  {
                    name: item.name,
                    sku: item.sku,
                    gtin: item.gtin,
                    price: asDecimal(item.salePrice),
                  },
                  template
                ),
              labels
            ),
            template
          )
        : renderLayoutSheetHtml(
            chosen.layout,
            repeated(
              (item) => ({
                name: item.name,
                sku: item.sku,
                gtin: item.gtin,
                // Formatado aqui: quem sabe formatar dinheiro é `@aeris/money`, e
                // o renderizador de layout recebe texto pronto.
                salePrice:
                  item.salePrice === null
                    ? null
                    : formatMoney(fromString(item.salePrice, UNIT_PRICE_SCALE)),
                salePriceAmount: item.salePrice,
                brand: item.brand ?? null,
                unit: item.unit ?? null,
                supplierReference: item.supplierReference ?? null,
                location: item.location ?? null,
                lastPurchaseAt: null,
              }),
              labels
            ),
            // F140: a data do selo de garantia é o dia em que ele sai.
            { skip, printedOn: printedOnToday() }
          )

  /* A prévia desenha a PRIMEIRA folha — não os 500 adesivos do lote. */
  const labelsOnSheet =
    chosen === undefined
      ? Math.min(total, 4)
      : sheets.isRoll
        ? 1
        : Math.min(total, perSheet - (startAt - 1))

  const print = (): void => {
    setError(null)
    printHtmlDocument(htmlFor(total, startAt - 1))
      .then(() => {
        // A próxima folha é nova: a posição volta ao começo.
        setStartText('1')
      })
      .catch((cause: unknown) => {
        setError(
          cause instanceof Error
            ? cause.message
            : 'Não foi possível abrir a impressão.'
        )
      })
  }

  return (
    <Page>
      <PageHeader
        title="Etiquetas"
        description="O que vai ser impresso. A fila fica guardada neste computador até imprimir ou limpar."
      />

      {/*
       * Escolher a etiqueta e bipar — nesta ordem, e ANTES da fila (F184).
       *
       * O dono: "pique aqui selecionando a etiqueta, depois posso bater vários
       * código e quantidade com a etiqueta selecionada, aí só imprimir". Por
       * isso as duas coisas ficam no topo e continuam na tela com a fila VAZIA:
       * era a fila vazia que trocava a página inteira por um aviso mandando a
       * pessoa ir ao catálogo — exatamente onde ela não quer ir.
       */}
      {tenantId === null ? null : (
        <section className="flex flex-col gap-2">
          <div className="flex flex-wrap items-end gap-3">
            {available.length === 0 ? null : (
              <Field
                label="Etiqueta"
                className="w-full max-w-72"
                hint="Fica escolhida para a próxima vez."
              >
                {(field) => (
                  <Select
                    {...field}
                    value={chosen?.id ?? ''}
                    onChange={(event) => {
                      const value = event.target.value
                      setChosenId(value)
                      lastTemplate.save({
                        templateId: value === '' ? null : value,
                      })
                    }}
                  >
                    {available.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.name}
                        {entry.isDefault ? ' (padrão)' : ''}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}

            {perSheet === 1 ? null : (
              <Field
                label="Começar na posição"
                className="w-40"
                hint={`1 a ${String(perSheet)} — folha já usada`}
                error={
                  wholeBetween(startText, 1, perSheet) === null
                    ? `De 1 a ${String(perSheet)}.`
                    : undefined
                }
              >
                {(field) => (
                  <Input
                    {...field}
                    inputMode="numeric"
                    value={startText}
                    onChange={(event) => {
                      setStartText(event.target.value)
                    }}
                  />
                )}
              </Field>
            )}

            <Field
              label="Bipe o código"
              className="min-w-64 flex-1"
              hint="Leia com o leitor ou digite e tecle Enter. Use 3*código para três etiquetas."
            >
              {(field) => (
                <Input
                  {...field}
                  ref={scanRef}
                  autoFocus
                  autoComplete="off"
                  className="font-(--font-mono) text-(length:--text-lg)"
                  placeholder="Código de barras ou código do produto"
                  value={scan}
                  onChange={(event) => {
                    setScan(event.target.value)
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') return
                    /*
                     * O leitor termina a leitura com Enter, e este campo não
                     * está num formulário justamente para essa tecla querer
                     * dizer "põe na fila".
                     */
                    event.preventDefault()
                    submitScan()
                  }}
                />
              )}
            </Field>
          </div>

          {scanning ? (
            <p className="text-(length:--text-xs) text-(--text-muted)">
              Procurando…
            </p>
          ) : null}
          {scanError === null ? null : (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {scanError}
            </p>
          )}
        </section>
      )}

      {items.length === 0 || tenantId === null ? (
        <EmptyState
          icon={<Tags aria-hidden="true" className="h-6 w-6" />}
          title="Nenhuma etiqueta na fila"
          description='Bipe um código acima, ou mande produtos para cá pelo botão "Etiqueta" no catálogo. A fila fica guardada neste computador.'
          action={
            <Button variant="secondary" asChild>
              <Link to="/produtos">Ir para o catálogo</Link>
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-4">
          {changedCount === 0 ? null : (
            <InlineAlert
              tone="warning"
              title={
                changedCount === 1
                  ? '1 produto mudou de preço desde que entrou na fila'
                  : `${String(changedCount)} produtos mudaram de preço desde que entraram na fila`
              }
            >
              A etiqueta sai com o preço de agora, que é o da prévia.
            </InlineAlert>
          )}

          {missingItems.length === 0 ? null : (
            <InlineAlert
              tone="danger"
              title={
                missingItems.length === 1
                  ? '1 produto não foi encontrado no catálogo'
                  : `${String(missingItems.length)} produtos não foram encontrados no catálogo`
              }
            >
              <span className="flex flex-wrap items-center gap-2">
                Inativo ou apagado: fica fora da impressão.
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    for (const item of missingItems) {
                      removeFromLabelQueue(tenantId, item.variantId)
                    }
                  }}
                >
                  Tirar da fila
                </Button>
              </span>
            </InlineAlert>
          )}

          {fresh.isError ? (
            <InlineAlert
              tone="warning"
              title="Não deu para conferir os preços agora"
            >
              <span className="flex flex-wrap items-center gap-2">
                A etiqueta sai com o preço de quando o produto entrou na fila.
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    void fresh.refetch()
                  }}
                >
                  Tentar de novo
                </Button>
              </span>
            </InlineAlert>
          ) : null}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Produto</TableHeaderCell>
                  <TableHeaderCell>Preço</TableHeaderCell>
                  <TableHeaderCell>Na fila desde</TableHeaderCell>
                  <TableHeaderCell>Etiquetas</TableHeaderCell>
                  <TableHeaderCell className="text-right">Ação</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((item) => (
                  <QueueRow
                    key={item.variantId}
                    item={item}
                    current={currentOf(item.variantId)}
                    missing={isMissing(item.variantId)}
                    tenantId={tenantId}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {error === null ? null : (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {error}
            </p>
          )}

          <section className="flex flex-col gap-2">
            <h2 className="text-(length:--text-sm) font-medium">
              Como vai sair
            </h2>
            {/*
             * Consulta de modelos que falhou NÃO pode cair calada no formato
             * simples: a pessoa conferiria a prévia achando que é o modelo da
             * loja e imprimiria uma folha inteira de adesivo no formato errado.
             * Dizer que o modelo não carregou é o que faz ela esperar ou
             * tentar de novo antes de gastar o rolo.
             */}
            {templates.isError ? (
              <p
                role="alert"
                className="flex flex-wrap items-center gap-2 text-(length:--text-sm) text-(--danger)"
              >
                Não foi possível carregar os modelos da loja — a prévia abaixo
                está no formato simples do Aeris.
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    void templates.refetch()
                  }}
                >
                  Tentar de novo
                </Button>
              </p>
            ) : null}

            <p className="text-(length:--text-xs) text-(--text-muted)">
              {chosen === undefined ? (
                <>
                  Formato simples do Aeris, {template.widthMm} ×{' '}
                  {template.heightMm} mm. Para usar os modelos da loja, importe
                  os arquivos do GDOOR em{' '}
                </>
              ) : (
                <>
                  {chosen.name} — {chosen.layout.label.widthMm} ×{' '}
                  {chosen.layout.label.heightMm} mm, {chosen.layout.columns}{' '}
                  {chosen.layout.columns === 1 ? 'coluna' : 'colunas'}. Os
                  modelos se administram em{' '}
                </>
              )}
              <Link
                to="/modelo-de-etiqueta"
                className="underline underline-offset-2"
              >
                Configurações → Etiqueta
              </Link>
              .
            </p>
            <LabelSheetPreview
              title="Pré-visualização das etiquetas"
              htmlFor={htmlFor}
              layout={chosen?.layout}
              simple={template}
              labelsOnSheet={labelsOnSheet}
              skip={startAt - 1}
            />
          </section>

          {/*
           * A barra (F195): o total, a ordem e os dois botões ficam à vista
           * enquanto 200 linhas rolam. Antes moravam no cabeçalho, e quem
           * conferia a fila até o fim tinha de voltar lá em cima para imprimir.
           */}
          <div
            role="toolbar"
            aria-label="Imprimir a fila"
            className="sticky bottom-0 z-10 -mx-4 flex flex-wrap items-center gap-3 border-t border-(--border-default) bg-(--surface-base) px-4 py-3"
          >
            <p
              aria-live="polite"
              className="min-w-48 flex-1 text-(length:--text-md) font-(--weight-semibold)"
            >
              {labelSheetSummary(total, sheets)}
            </p>
            <label className="flex items-center gap-2 text-(length:--text-sm) text-(--text-secondary)">
              Ordem
              <Select
                value={order}
                className="w-36"
                onChange={(event) => {
                  const next =
                    LABEL_QUEUE_ORDERS.find(
                      (entry) => entry.value === event.target.value
                    )?.value ?? 'arrival'
                  setOrder(next)
                  saveOrder(next)
                }}
              >
                {LABEL_QUEUE_ORDERS.map((entry) => (
                  <option key={entry.value} value={entry.value}>
                    {entry.label}
                  </option>
                ))}
              </Select>
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setConfirmingClear(true)
              }}
              leadingIcon={<Trash2 aria-hidden="true" className="h-4 w-4" />}
            >
              Limpar
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={fresh.isPending || printable.length === 0}
              onClick={print}
              leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
            >
              {fresh.isPending
                ? 'Conferindo preços…'
                : `Imprimir ${String(total)} etiqueta${total === 1 ? '' : 's'}`}
            </Button>
          </div>

          <PrintSettingsHint layout={chosen?.layout} />

          {/*
           * Limpar pede confirmação (F195): era um clique para 200 linhas
           * sumirem, sem desfazer — e a fila é o trabalho de um dia de bipe.
           */}
          <ConfirmDialog
            open={confirmingClear}
            onOpenChange={setConfirmingClear}
            title="Limpar a fila"
            description={`Tirar ${items.length === 1 ? '1 produto' : `${String(items.length)} produtos`} (${String(labelCount(items))} ${labelCount(items) === 1 ? 'etiqueta' : 'etiquetas'}) da fila? Não dá para desfazer.`}
            confirmLabel="Limpar a fila"
            cancelLabel="Voltar"
            tone="danger"
            onConfirm={() => {
              clearLabelQueue(tenantId)
              setConfirmingClear(false)
            }}
          />
        </div>
      )}
    </Page>
  )
}
